import {HttpClient, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Observable, map} from 'rxjs';
import {environment} from '../../../environments/environment';
import {ApiService} from './api.service';
import {FeedbackFormSettingsUpdateRequest} from '../types/generated/feedbackFormSettingsUpdateRequest';
import {FeedbackFormSettingsResource} from '../types/generated/feedbackFormSettingsResource';
import {feedbackFormListQuery} from '../types/feedback-types/feedbackFormListQuery.interface';
import {FeedbackFormListResource} from '../types/generated/feedbackFormListResource';
import {FeedbackFormStructureResource} from '../types/generated/feedbackFormStructureResource';
import {FeedbackFormStructureUpdateRequest} from '../types/generated/feedbackFormStructureUpdateRequest';
import {FeedbackFormRRuleValidationResource} from '../types/generated/feedbackFormRRuleValidationResource';
import {FeedbackFormRRuleValidationRequest} from '../types/generated/feedbackFormRRuleValidationRequest';
import {FeedbackInstanceListResource} from '../types/generated/feedbackInstanceListResource';
import {FeedbackFormAttemptListResource} from '../types/generated/feedbackFormAttemptListResource';
import {
  FeedbackAnswerUpdateRequest,
  FeedbackAnswerUpdateResource,
  FeedbackAttemptListResource,
  FeedbackAttemptStateResource,
  FeedbackFormStatisticsResource,
  FeedbackFormPresentationResource,
  FeedbackQuestionStatisticsResource,
} from '../types/generated';
import {TokenService} from './token.service';
import {PublicFeedbackActiveInstanceResource} from '../types/feedback-types/publicFeedbackActiveInstanceResource.interface';
import {FeedbackAttemptResultResource} from '../types/generated/feedbackAttemptResultResource';
import {FeedbackStatisticsQuery} from '../types/feedback-types/feedbackStatisticsQuery.interface';
import {FeedbackInstanceActiveResource} from '../types/generated/feedbackInstanceActiveResource';
import {FileStorageResource} from '../types/generated/fileStorageResource';

export interface FeedbackRequestContext {
  publicToken?: string | null;
  attemptToken?: string | null;
  lockToken?: string | null;
}

export interface FeedbackAttemptStartResponse {
  attempt: FeedbackAttemptStateResource;
  attemptToken: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class FeedbackApiService {
  private http: HttpClient = inject(HttpClient);
  private api: ApiService = inject(ApiService);
  private tokenService: TokenService = inject(TokenService);

  private baseUrl(): string {
    return `${this.api.getUrl()}/${environment.api.version}`;
  }

  //#region admin section
  public getFormList(queryOptions?: feedbackFormListQuery): Observable<FeedbackFormListResource[]> {
    let params = new HttpParams();

    for (const [key, value] of Object.entries(queryOptions ?? {})) {
      if (value !== undefined && value !== null) {
        params = params.set(key, value);
      }
    }

    return this.http.get<FeedbackFormListResource[]>(`${this.baseUrl()}/feedback/forms`, {
      headers: this.createHeaders(),
      params,
    });
  }

  public getSubmittedFormAttempts(
    formId: number,
    userIds: number[],
    groupIds: number[],
    instanceIds: number[],
    limit: number,
    skip: number,
    anonymous?: boolean,
  ): Observable<FeedbackFormAttemptListResource[]> {
    let params = new HttpParams();

    for (const instanceId of instanceIds) {
      params = params.append('instanceIds[]', instanceId);
    }

    for (const userId of userIds) {
      params = params.append('userIds[]', userId);
    }

    for (const groupId of groupIds) {
      params = params.append('groupIds[]', groupId);
    }

    params = params.set('limit', limit);
    params = params.set('skip', skip);

    if(anonymous !== undefined) {
      params = params.set('anonymous', anonymous);
    }

    return this.http.get<FeedbackFormAttemptListResource[]>(
      `${this.baseUrl()}/feedback/forms/${formId}/attempts/submitted`,
      {
        headers: this.createHeaders(),
        params,
      }
    );
  }

  public getFormStatistics(formId: number, query: FeedbackStatisticsQuery): Observable<FeedbackFormStatisticsResource> {
    return this.http.get<FeedbackFormStatisticsResource>(
      `${this.baseUrl()}/feedback/forms/${formId}/statistics`,
      {
        headers: this.createHeaders(),
        params: this.createStatisticsQueryParams(query),
      }
    );
  }

  public getQuestionStatistics(
    formId: number,
    questionId: number,
    query: FeedbackStatisticsQuery
  ): Observable<FeedbackQuestionStatisticsResource> {
    return this.http.get<FeedbackQuestionStatisticsResource>(
      `${this.baseUrl()}/feedback/forms/${formId}/questions/${questionId}/statistics`,
      {
        headers: this.createHeaders(),
        params: this.createStatisticsQueryParams(query),
      }
    );
  }

  public getFormSetting(id: string | null): Observable<FeedbackFormSettingsResource> {
    return this.http.get<FeedbackFormSettingsResource>(
      `${this.baseUrl()}/feedback/forms/${id}/settings`,
      {headers: this.createHeaders()}
    );
  }

  public createFormSetting(body: FeedbackFormSettingsUpdateRequest): Observable<FeedbackFormSettingsResource> {
    return this.http.post<FeedbackFormSettingsResource>(
      `${this.baseUrl()}/feedback/forms`,
      body,
      {headers: this.createHeaders()}
    );
  }

  public updateFormSetting(
    id: string | null,
    body: FeedbackFormSettingsUpdateRequest,
    lockToken: string | null = null
  ): Observable<FeedbackFormSettingsResource> {
    return this.http.put<FeedbackFormSettingsResource>(
      `${this.baseUrl()}/feedback/forms/${id}/settings`,
      body,
      {headers: this.createHeaders({lockToken})}
    );
  }

  public deleteForm(id: string, lockToken: string | null = null): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl()}/feedback/forms/${id}`,
      {headers: this.createHeaders({lockToken})}
    );
  }

  public publishForm(id: string, lockToken: string | null = null): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl()}/feedback/forms/${id}/publish`,
      {},
      {headers: this.createHeaders({lockToken})}
    );
  }

  public validateFormSettingsRecursionRule(body: FeedbackFormRRuleValidationRequest): Observable<FeedbackFormRRuleValidationResource> {
    return this.http.post<FeedbackFormRRuleValidationResource>(
      `${this.baseUrl()}/feedback/forms/validation/rrule`,
      body,
      {headers: this.createHeaders()}
    );
  }

  public getStructure(id: string): Observable<FeedbackFormStructureResource> {
    return this.http.get<FeedbackFormStructureResource>(
      `${this.baseUrl()}/feedback/forms/${id}/structure`,
      {headers: this.createHeaders()}
    );
  }

  public updateStructure(id: string, body: FeedbackFormStructureUpdateRequest, lockToken: string | null = null): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl()}/feedback/forms/${id}/structure`,
      body,
      {headers: this.createHeaders({lockToken})}
    );
  }

  public lockForm(formId: string, lockToken: string | null): Observable<{lock_token: string}> {
    return this.http.put<{lock_token: string}>(
      `${this.baseUrl()}/feedback/forms/${formId}/lock`,
      lockToken === null ? {} : {lock_token: lockToken},
      {headers: this.createHeaders()}
    );
  }

  public unlockForm(formId: string, lockToken: string): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl()}/feedback/forms/${formId}/lock/release`,
      {lock_token: lockToken},
      {headers: this.createHeaders()}
    );
  }

  public unlockFormOnUnload(formId: string, lockToken: string): void {
    fetch(`${this.baseUrl()}/feedback/forms/${formId}/lock/release`, {
      method: 'PUT',
      headers: this.toFetchHeaders(this.createHeaders()),
      body: JSON.stringify({lock_token: lockToken}),
      keepalive: true,
    });
  }
  //#endregion admin section

  //#region consumer section
  public getAvailableInstances(
    mandatory: boolean,
    search: string | null,
    limit: number | null,
    skip: number | null
  ): Observable<FeedbackInstanceListResource> {
    let params = new HttpParams().set('mandatory', mandatory);

    if (search !== null) {
      params = params.set('search', search);
    }

    if (limit !== null) {
      params = params.set('limit', limit);
    }

    if (skip !== null) {
      params = params.set('skip', skip);
    }

    return this.http.get<FeedbackInstanceListResource>(
      `${this.baseUrl()}/feedback/instances/available`,
      {
        headers: this.createHeaders(),
        params,
      }
    );
  }

  public getSubmittedAttempts(
    anonymous: boolean | null,
    mandatory: boolean | null,
    search: string | null,
    limit: number | null,
    skip: number | null
  ): Observable<FeedbackAttemptListResource> {
    let params = new HttpParams();
    if (anonymous !== null) {
      params = params.set('anonymous', anonymous);
    }
    if (mandatory !== null) {
      params = params.set('mandatory', mandatory);
    }

    if (search !== null) {
      params = params.set('search', search);
    }

    if (limit !== null) {
      params = params.set('limit', limit);
    }

    if (skip !== null) {
      params = params.set('skip', skip);
    }

    return this.http.get<FeedbackAttemptListResource>(
      `${this.baseUrl()}/feedback/attempts/submitted`,
      {
        headers: this.createHeaders(),
        params,
      }
    );
  }

  public getActiveInstances(formId: number, requestContext: FeedbackRequestContext): Observable<PublicFeedbackActiveInstanceResource[]> {
    return this.http.get<PublicFeedbackActiveInstanceResource[]>(
      `${this.baseUrl()}/feedback/forms/${formId}/instances/active`,
      {
        headers: this.createHeaders({publicToken: requestContext.publicToken}),
      }
    );
  }

  public getFormActiveInstances(formId: number): Observable<FeedbackInstanceActiveResource[]> {
    return this.http.get<FeedbackInstanceActiveResource | FeedbackInstanceActiveResource[] | null>(
      `${this.baseUrl()}/feedback/forms/${formId}/instances/active`,
      {
        headers: this.createHeaders(),
      }
    ).pipe(
      map((result) => Array.isArray(result) ? result : (result ? [result] : []))
    );
  }

  public getFormInstances(formId: number): Observable<FeedbackInstanceActiveResource[]> {
    return this.http.get<FeedbackInstanceActiveResource | FeedbackInstanceActiveResource[] | null>(
      `${this.baseUrl()}/feedback/forms/${formId}/instances`,
      {
        headers: this.createHeaders(),
      }
    ).pipe(
      map((result) => Array.isArray(result) ? result : (result ? [result] : []))
    );
  }

  public startAttempt(instanceId: number, requestContext?: FeedbackRequestContext): Observable<FeedbackAttemptStartResponse> {
    return this.http.post<FeedbackAttemptStateResource>(
      `${this.baseUrl()}/feedback/instances/${instanceId}/attempts`,
      {},
      {
        headers: this.createHeaders(requestContext),
        observe: 'response',
      }
    ).pipe(
      map((response: HttpResponse<FeedbackAttemptStateResource>) => {
        if (response.body === null) {
          throw new Error('Missing feedback attempt response body');
        }

        return {
          attempt: response.body,
          attemptToken: this.normalizeToken(response.headers.get('X-Feedback-Attempt-Token')),
        };
      })
    );
  }

  public remindFeedbackInstance(instanceId: number): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl()}/feedback/instances/${instanceId}/remind`,
      {},
      {
        headers: this.createHeaders(),
      }
    );
  }

  public getFormPresentation(formId: number, requestContext?: FeedbackRequestContext): Observable<FeedbackFormPresentationResource> {
    return this.http.get<FeedbackFormPresentationResource>(
      `${this.baseUrl()}/feedback/forms/${formId}/presentation`,
      {
        headers: this.createHeaders({publicToken: requestContext?.publicToken}),
      }
    );
  }

  public getAttemptResult(attemptId: number, requestContext?: FeedbackRequestContext): Observable<FeedbackAttemptResultResource> {
    return this.http.get<FeedbackAttemptResultResource>(
      `${this.baseUrl()}/feedback/attempts/${attemptId}/result`,
      {
        headers: this.createHeaders(requestContext),
      }
    );
  }

  public lockAttempt(
    attemptId: string,
    previousLockToken: string | null,
    requestContext?: FeedbackRequestContext
  ): Observable<{lock_token: string}> {
    return this.http.put<{lock_token: string}>(
      `${this.baseUrl()}/feedback/attempts/${attemptId}/lock`,
      previousLockToken === null ? {} : {lock_token: previousLockToken},
      {
        headers: this.createHeaders(requestContext),
      }
    );
  }

  public unlockAttempt(attemptId: string, lockToken: string, requestContext?: FeedbackRequestContext): Observable<void> {
    return this.http.put<void>(
      `${this.baseUrl()}/feedback/attempts/${attemptId}/lock/release`,
      {lock_token: lockToken},
      {
        headers: this.createHeaders(
          {attemptToken: requestContext?.attemptToken},
          {includeAuthorization: !this.hasAttemptToken(requestContext)}
        ),
      }
    );
  }

  public unlockAttemptOnUnload(attemptId: string, lockToken: string, requestContext?: FeedbackRequestContext): void {
    fetch(`${this.baseUrl()}/feedback/attempts/${attemptId}/lock/release`, {
      method: 'PUT',
      headers: this.toFetchHeaders(
        this.createHeaders(
          {attemptToken: requestContext?.attemptToken},
          {includeAuthorization: !this.hasAttemptToken(requestContext)}
        )
      ),
      body: JSON.stringify({lock_token: lockToken}),
      keepalive: true,
    });
  }

  public updateAttemptAnswer(
    attemptId: number,
    questionId: number,
    body: FeedbackAnswerUpdateRequest,
    requestContext: FeedbackRequestContext
  ): Observable<FeedbackAnswerUpdateResource> {
    return this.http.put<FeedbackAnswerUpdateResource>(
      `${this.baseUrl()}/feedback/attempts/${attemptId}/answers/${questionId}`,
      body,
      {headers: this.createHeaders(requestContext)}
    );
  }

  public uploadAttemptAnswerFile(
    attemptId: number,
    questionId: number,
    file: File,
    requestContext: FeedbackRequestContext
  ): Observable<FileStorageResource> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<FileStorageResource>(
      `${this.baseUrl()}/feedback/attempts/${attemptId}/answers/${questionId}/files`,
      formData,
      {
        headers: this.createHeaders(requestContext, {includeJsonContentType: false}),
      }
    );
  }

  public detachAttemptAnswerFile(
    attemptId: number,
    questionId: number,
    fileUuid: string,
    requestContext: FeedbackRequestContext
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl()}/feedback/attempts/${attemptId}/answers/${questionId}/files/${encodeURIComponent(fileUuid)}`,
      {
        headers: this.createHeaders(requestContext),
      }
    );
  }

  public submitAttempt(attemptId: number, requestContext: FeedbackRequestContext): Observable<FeedbackAttemptResultResource> {
    return this.http.put<FeedbackAttemptResultResource>(
      `${this.baseUrl()}/feedback/attempts/${attemptId}/submit`,
      {},
      {headers: this.createHeaders(requestContext)}
    );
  }
  //#endregion consumer section

  private createHeaders(
    requestContext?: FeedbackRequestContext,
    options: {includeAuthorization?: boolean; includeJsonContentType?: boolean} = {}
  ): HttpHeaders {
    let headers = this.api.getHeaders();

    if (options.includeJsonContentType === false) {
      headers = headers.delete('Content-Type');
    }

    if (options.includeAuthorization !== false) {
      const authToken = this.normalizeToken(this.tokenService.getToken());
      if (authToken !== null) {
        headers = headers.set('Authorization', `Bearer ${authToken}`);
      }
    }

    const publicToken = this.normalizeToken(requestContext?.publicToken);
    if (publicToken !== null) {
      headers = headers.set('X-Feedback-Public-Token', publicToken);
    }

    const attemptToken = this.normalizeToken(requestContext?.attemptToken);
    if (attemptToken !== null) {
      headers = headers.set('X-Feedback-Attempt-Token', attemptToken);
    }

    const lockToken = this.normalizeToken(requestContext?.lockToken);
    if (lockToken !== null) {
      headers = headers.set('X-Lock-Token', lockToken);
    }

    return headers;
  }

  private toFetchHeaders(httpHeaders: HttpHeaders): Headers {
    const headers = new Headers();
    for (const key of httpHeaders.keys()) {
      for (const value of httpHeaders.getAll(key) ?? []) {
        headers.append(key, value);
      }
    }

    return headers;
  }

  private normalizeToken(token: string | null | undefined): string | null {
    const normalizedToken = token?.trim() ?? '';
    return normalizedToken === '' ? null : normalizedToken;
  }

  private hasAttemptToken(requestContext?: FeedbackRequestContext): boolean {
    return this.normalizeToken(requestContext?.attemptToken) !== null;
  }

  private createStatisticsQueryParams(query: FeedbackStatisticsQuery): HttpParams {
    let params = new HttpParams().set('maxBuckets', query.maxBuckets);

    for (const instanceId of query.instanceIds ?? []) {
      params = params.append('instanceIds[]', instanceId);
    }

    for (const userId of query.userIds ?? []) {
      params = params.append('userIds[]', userId);
    }

    for (const groupId of query.groupIds ?? []) {
      params = params.append('groupIds[]', groupId);
    }

    return params;
  }
}
